/*
 * Checkout Flow — <cf-integration> : la scène suit la lecture des trois chapitres (défilement natif).
 * Chapitre actif = celui qui traverse le milieu de l'écran. Les onglets servent de contrôle manuel.
 * Mouvement réduit : états finaux de chaque chapitre, sans interpolation.
 */
(() => {
  const start = () => {
    const CF = window.CF;
    if (!CF || customElements.get('cf-integration')) return;
    const { clamp, seg, lerp, motion, loop, observeVisible, onScroll, track, live, firstBind, release, whenNear, scroller } = CF;

    const FEAT_POS = [[-205, -150, 150], [205, -150, 170], [-215, 150, 160], [210, 150, 170], [0, 222, 150]];
    const CHIP_POS = [[-160, -205, 110], [175, -150, 140], [150, 170, 120]];

    class CFIntegration extends HTMLElement {
      connectedCallback() {
        if (!live(this)) return; // simple déplacement dans l'éditeur
        this.section = this.closest('.cf-int');
        this.seqEl = this.closest('.cf-int__seq');
        this.seq = !!this.section && this.section.classList.contains('cf-int--seq');
        this.stage = this.querySelector('.cf-int__stage');
        this.tilt = this.querySelector('.cf-int__tilt');
        // mise à l'échelle immédiate (peu coûteuse) ; scène animée initialisée à l'approche de l'écran
        this.scale = 1;
        this.measure = () => {
          const r = this.stage.getBoundingClientRect();
          this.scale = Math.min(1, r.width / 560, r.height / 600);
          this.applyTilt();
        };
        this.ro = new ResizeObserver(this.measure);
        this.ro.observe(this.stage);
        this.measure();
        this.stopNear = whenNear(this, () => this.init());
      }
      init() {
        this.chapters = [...this.querySelectorAll('.cf-int__chapter')];
        this.tabs = [...this.querySelectorAll('.cf-int__tab')];
        this.bars = this.tabs.map((t) => t.querySelector('b'));
        this.el = {};
        this.querySelectorAll('[data-el]').forEach((n) => { this.el[n.getAttribute('data-el')] = n; });
        this.q = 0; this.target = 0; this.chap = -1; this.visible = false;
        this.tick = this.tick.bind(this);
        this.classList.add('is-live');
        this.unsub = onScroll(() => this.read());
        this.stopVis = observeVisible(this, (v) => { this.visible = v; this.sync(); });
        this.onMotion = () => this.sync();
        document.addEventListener('cf:motion-change', this.onMotion);
        if (firstBind(this)) this.tabs.forEach((tab, i) => tab.addEventListener('click', (e) => {
          const ch = this.chapters[i];
          if (!ch) return;
          e.preventDefault();
          const behavior = motion() === 'off' ? 'auto' : 'smooth';
          if (this.seqOn()) {
            // séquence : on défile jusqu'au chapitre dans la section fixée
            const sc = scroller();
            const total = this.seqEl.offsetHeight - window.innerHeight;
            const top = sc.scrollTop + this.seqEl.getBoundingClientRect().top + total * ((i + 0.6) / this.chapters.length);
            sc.scrollTo({ top, behavior });
          } else ch.scrollIntoView({ behavior, block: 'center' });
          track('integration_tab', { chapter: i + 1 });
        }));
        if (window.matchMedia('(pointer: fine)').matches) {
          this.onMove = (e) => {
            if (motion() !== 'full' || window.innerWidth < 990) return;
            const r = this.stage.getBoundingClientRect();
            this.rx = ((e.clientY - r.top) / r.height - 0.5) * -8;
            this.ry = ((e.clientX - r.left) / r.width - 0.5) * 10;
            this.applyTilt();
          };
          this.onLeave = () => { this.rx = 0; this.ry = 0; this.applyTilt(); };
          this.stage.addEventListener('pointermove', this.onMove);
          this.stage.addEventListener('pointerleave', this.onLeave);
        }
        this.read();
        this.q = this.target;
        this.render(performance.now());
      }

      disconnectedCallback() {
        release(this, () => {
          if (this.stopNear) this.stopNear();
          loop.remove(this.tick); this.running = false;
          if (this.ro) this.ro.disconnect();
          if (this.unsub) this.unsub();
          if (this.stopVis) this.stopVis();
          document.removeEventListener('cf:motion-change', this.onMotion);
          if (this.onMove) { this.stage.removeEventListener('pointermove', this.onMove); this.stage.removeEventListener('pointerleave', this.onLeave); }
        });
      }

      applyTilt() {
        const rot = this.rx || this.ry ? ` rotateX(${(this.rx || 0).toFixed(2)}deg) rotateY(${(this.ry || 0).toFixed(2)}deg)` : '';
        this.tilt.style.transform = `scale(${this.scale.toFixed(3)})${rot}`;
      }

      seqOn() { return this.seq && !!this.seqEl && motion() !== 'off'; }

      /* progression globale 0 → 1 : avancée dans la section fixée (séquence) ou position des chapitres (standard) */
      read() {
        if (this.seqOn()) {
          const r = this.seqEl.getBoundingClientRect();
          const total = this.seqEl.offsetHeight - window.innerHeight;
          this.target = total > 0 ? clamp(-r.top / total, 0, 1) : 0;
          if (!this.running) { this.q = this.target; this.render(performance.now()); }
          return;
        }
        const vh = window.innerHeight;
        const mid = window.innerWidth < 990 ? vh * 0.72 : vh * 0.5;
        const n = this.chapters.length;
        let q = 0;
        for (let i = 0; i < n; i++) {
          const r = this.chapters[i].getBoundingClientRect();
          if (mid >= r.top) q = i + clamp((mid - r.top) / Math.max(1, r.height), 0, 1);
        }
        this.target = clamp(q / n, 0, 1);
        if (!this.running) { this.q = this.target; this.render(performance.now()); }
      }

      sync() {
        const anim = this.visible && motion() !== 'off';
        if (anim && !this.running) { this.running = true; loop.add(this.tick); }
        else if (!anim && this.running) { this.running = false; loop.remove(this.tick); this.q = this.target; this.render(performance.now()); }
      }

      tick(t) {
        this.q += (this.target - this.q) * loop.k(0.12);
        if (Math.abs(this.target - this.q) < 0.0004) this.q = this.target;
        this.render(t);
      }

      place(name, x, y, z, o, extra = '') {
        const n = this.el[name];
        if (!n) return;
        n.style.opacity = o.toFixed(3);
        n.style.visibility = o > 0.002 ? 'visible' : 'hidden';
        n.style.transform = `translate3d(${x.toFixed(1)}px,${y.toFixed(1)}px,${z.toFixed(1)}px)${extra}`;
      }

      type(name, amount) {
        const n = this.el[name];
        if (!n) return;
        const full = n.getAttribute('data-full') || '';
        const txt = full.slice(0, Math.round(full.length * clamp(amount, 0, 1)));
        if (n.textContent !== txt) n.textContent = txt;
        n.parentNode.classList.toggle('is-focus', amount > 0 && amount < 1);
      }

      render(t) {
        const stepped = motion() === 'off';
        let q = this.q;
        const n = this.chapters.length || 3;
        const ch = clamp(Math.floor(q * n), 0, n - 1);
        if (stepped) q = (ch + (ch === 2 ? 1 : 0.62)) / n; // états finaux lisibles
        if (ch !== this.chap) {
          this.chap = ch;
          this.setAttribute('data-chapter', String(ch));
          this.chapters.forEach((el, k) => { el.classList.toggle('is-active', k === ch); el.classList.toggle('is-past', k < ch); });
          this.tabs.forEach((tab, k) => {
            tab.classList.toggle('is-active', k === ch);
            if (k === ch) tab.setAttribute('aria-current', 'step'); else tab.removeAttribute('aria-current');
          });
          if (ch === 2) track('integration_connected_seen', {}, true);
        }
        this.bars.forEach((b, k) => { if (b) b.style.width = `${(clamp(q * n - k, 0, 1) * 100).toFixed(1)}%`; });
        const time = stepped ? 0 : t;
        const fl = Math.sin(time * 0.001) * 6;

        /* 1 — Shopify */
        const s1in = seg(q, 0, 0.05), s1out = seg(q, 0.29, 0.36);
        this.place('shop', -50 * s1out, fl - 20 + (1 - s1in) * 40, lerp(-200, 0, s1in) - s1out * 220, s1in * (1 - s1out),
          ` rotateY(${lerp(-18, -6, clamp(q * 3, 0, 1)).toFixed(2)}deg) rotateX(6deg)`);
        if (this.el.orders) this.el.orders.textContent = String(Math.round(128 * seg(q, 0.02, 0.28)));
        CHIP_POS.forEach((p, k) => {
          const a = seg(q, 0.05 + k * 0.05, 0.12 + k * 0.05) * (1 - s1out);
          this.place('chip' + (k + 1), p[0] + (1 - a) * (p[0] > 0 ? 50 : -50), p[1] + Math.sin(time * 0.0012 + k) * 6, p[2], a);
        });

        /* 2 — SumUp */
        const s2in = seg(q, 0.31, 0.42), s2out = seg(q, 0.62, 0.69), o2 = s2in * (1 - s2out);
        const rotY = lerp(-70, -20, s2in) + seg(q, 0.42, 0.62) * 34 + Math.sin(time * 0.0009) * 4;
        this.place('card', lerp(170, 0, s2in) - s2out * 60, fl * 1.3 + 16, lerp(-300, 40, s2in), o2,
          ` rotateY(${rotY.toFixed(2)}deg) rotateZ(${lerp(-14, -3, s2in).toFixed(2)}deg) scale(${lerp(1, 0.6, s2out).toFixed(3)})`);
        this.place('sumlogo', 0, (1 - s2in) * 20 - 28, 60, seg(q, 0.36, 0.44) * (1 - s2out));
        FEAT_POS.forEach((p, k) => {
          const a = seg(q, 0.4 + k * 0.035, 0.46 + k * 0.035) * (1 - s2out);
          this.place('feat' + (k + 1), p[0] + (1 - a) * (p[0] > 0 ? 40 : -40), p[1] + Math.sin(time * 0.0011 + k * 1.3) * 5, p[2], a);
        });

        /* 3 — la connexion, puis le paiement en action */
        const l = clamp((q * 3 - 2) / 0.72, 0, 1); // la séquence de paiement se termine avant la fin du chapitre
        const s3 = seg(q, 0.66, 0.76);
        const up = seg(l, 0.3, 0.45);
        const gap = lerp(lerp(250, 150, s3), 128, up);
        const ty = lerp(0, -214, up);
        const ts = lerp(1, 0.62, up);
        this.place('tshop', -gap, ty + fl * (1 - up), 60, s3, ` rotateY(${lerp(45, 12, s3).toFixed(2)}deg) scale(${ts.toFixed(3)})`);
        this.place('tsum', gap, ty - fl * (1 - up), 60, s3, ` rotateY(${lerp(-45, -12, s3).toFixed(2)}deg) scale(${ts.toFixed(3)})`);
        const bm = seg(q, 0.72, 0.8);
        this.place('beam', 0, ty, 30, bm, ` scaleX(${(bm * lerp(1, 0.78, up)).toFixed(3)})`);
        const cf = seg(q, 0.76, 0.84);
        this.place('core', 0, ty, 110, cf, ` scale(${(lerp(0.3, 1, cf) * lerp(1, 0.78, up)).toFixed(3)}) rotate(${((1 - cf) * -90).toFixed(1)}deg)`);
        if (this.el.pk1) {
          const u1 = stepped ? 0.3 : (time * 0.0005) % 1, u2 = stepped ? 0.7 : (time * 0.0005 + 0.5) % 1;
          this.el.pk1.style.transform = `translateX(${(u1 * 300).toFixed(1)}px)`;
          this.el.pk2.style.transform = `translateX(${(u2 * 300).toFixed(1)}px)`;
          this.el.pk1.style.opacity = this.el.pk2.style.opacity = (cf * (1 - up * 0.2)).toFixed(3);
        }
        const co = seg(l, 0.34, 0.5);
        this.place('checkout', 0, lerp(60, 34, co), lerp(-120, 20, co), co, ` rotateX(${lerp(12, 3, co).toFixed(2)}deg)`);
        this.type('cardnum', seg(l, 0.5, 0.62));
        this.type('exp', seg(l, 0.62, 0.66));
        this.type('cvc', seg(l, 0.66, 0.69));
        if (this.el.pay) this.el.pay.setAttribute('data-state', l > 0.78 ? '2' : l > 0.71 ? '1' : '0');
        const n1 = seg(l, 0.8, 0.88), n2 = seg(l, 0.87, 0.95);
        this.place('n1', lerp(-190, -150, n1), -118, 160, n1);
        this.place('n2', lerp(190, 150, n2), 190, 180, n2);
      }
    }
    customElements.define('cf-integration', CFIntegration);
  };
  if (window.CF) start(); else document.addEventListener('cf:ready', start, { once: true });
})();
